<template>
    <div>
        <div class="d-flex justify-content-between align-items-end mb-2">
            <div>
                <h4>Request Court Order</h4>
            </div>
        </div>

        <validation-observer ref="validationObserver" v-slot="vo">
            <!--Ordering Client / Client Reference-->
            <b-form-row>
                <b-form-group label="Ordering Client"
                              label-for="orderingClient"
                              class="col-md-4">
                    <b-form-input name="orderingClient"
                                  maxlength="500"
                                  :disabled="true"
                                  v-model="orderingClient">
                    </b-form-input>
                </b-form-group>
                <b-form-group label="Ordering Client Contact"
                              label-for="orderingClientContact"
                              class="col-md-4">
                    <b-form-input name="orderingClientContact"
                                  :disabled="true"
                                  v-model="orderingClientContact">
                    </b-form-input>
                </b-form-group>
                <b-form-group label="Client Reference"
                              label-for="clientReference"
                              class="col-md-4">
                    <b-form-input name="clientReference"
                                  maxlength="500"
                                  :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                  v-model="clientReference">
                    </b-form-input>
                    <b-tooltip target="clientReference" triggers="hover" placement="bottom" variant="dark" v-if="clientReference">
                        {{ clientReference }}
                    </b-tooltip>
                </b-form-group>
            </b-form-row>
            <b-form-row>
                <validation-provider rules="required" name="Ordering Client Branch/Location" v-slot="vp" slim>
                    <b-form-group label="Ordering Client Branch/Location"
                                  label-for="orderingClientBranchId"
                                  class="col-md-4">
                        <select2 name="orderingClientBranchId"
                                 :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                 :options="selectableClientBranches"
                                 key-property="clientBranchId"
                                 text-property="name"
                                 placeholder="Select a client branch"
                                 v-model="orderingClientBranchId"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <b-form-group class="col-md-4">
                    <b-form-checkbox name="separateBillingContact"
                                     v-model="separateBillingContact"
                                     class="mb-2"
                                     switch
                                     :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC">
                        Separate billing contact
                    </b-form-checkbox>
                    <validation-provider rules="required" name="Billing Contact" v-slot="vp" v-if="separateBillingContact">
                        <contact-select name="billingContactId"
                                        :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                        :api-url="$urlHelper.getApiUrl('contact', 'billing')"
                                        :ref-data-api-controller-url="$urlHelper.getApiUrl('referencedata')"
                                        :mandatory-contact-properties="mandatoryContactProperties"
                                        :client-id="orderingClientId"
                                        v-model="billingContactId"
                                        :initial-contacts="initialBillingContact ? [ initialBillingContact ] : null"
                                        :state="vp.errors[0] ? false : null">
                        </contact-select>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </validation-provider>
                </b-form-group>
                <b-form-group label="Request Number"
                              label-for="orderRequestNumber"
                              class="col-md-2">
                    <b-form-input name="orderRequestNumber"
                                  :value="isUuidNullOrEmpty(orderRequest.clientOrderRequestId) ? null : orderRequest.orderRequestNumberDisplayText"
                                  readonly>
                    </b-form-input>
                </b-form-group>
                <b-form-group label="Status"
                              label-for="orderRequestStatus"
                              class="col-md-2">
                    <b-form-input name="orderRequestStatus"
                                  :value="isUuidNullOrEmpty(orderRequest.clientOrderRequestId) ? null : orderRequest.statusDisplayName"
                                  readonly>
                    </b-form-input>
                </b-form-group>
            </b-form-row>
            <b-form-row>
                <validation-provider ref="courtRegistrySelectValidator" rules="required" name="Court/Tribunal/Other" v-slot="vp" slim>
                    <b-form-group label="Court/Tribunal/Other"
                                  label-for="courtRegistryId"
                                  class="col-md-4">
                        <select2 name="courtRegistryId"
                                 :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                 v-model="courtRegistryId"
                                 :options="allCourtRegistries"
                                 disable-search
                                 placeholder="Select a court/tribunal/other"
                                 v-on:select="courtRegistrySelected"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <b-form-group label="Jurisdiction"
                              label-for="courtJurisdictionId"
                              class="col-md-4">
                    <select2 name="courtJurisdictionId"
                             :disabled="readonly || !canEditOrderRequest || isUuidNullOrEmpty(courtRegistryId) || orderRequest.submittedDateTimeUTC"
                             :options="initialCourtJurisdictions"
                             enable-ajax
                             :placeholder="(readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC) ? null : isUuidNullOrEmpty(courtRegistryId) ? 'Select a court/tribunal/other first' : 'Select a jurisdiction'"
                             key-property="courtJurisdictionId"
                             text-property="name"
                             v-model="courtJurisdictionId"
                             v-on:search="findJurisdictions"
                             v-on:select="courtJurisdictionSelected">
                    </select2>
                </b-form-group>
                <validation-provider ref="clientParticipationInMatterSelectValidator" rules="required" :name="$t('Entities.Shared.ClientParticipationInMatter')" v-slot="vp" slim>
                    <b-form-group :label="$t('Entities.Shared.ClientParticipationInMatter')"
                                  label-for="clientParticipationInMatter"
                                  class="col-md-4">
                        <select2 name="clientParticipationInMatter"
                                 :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                 v-model="clientParticipationInMatter"
                                 :options="allCourtOrderClientParticipationInMatters"
                                 disable-search
                                 placeholder="Select how the client participates"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
            </b-form-row>
            <b-form-row>
                <b-form-group :label="$t('Entities.Shared.MatterNumber')"
                              label-for="matterNumber"
                              class="col-md-3">
                    <b-form-input name="matterNumber"
                                  maxlength="500"
                                  :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                  v-model="matterNumber">
                    </b-form-input>
                </b-form-group>
                <validation-provider ref="matterNameValidator" rules="required" :name="$t('Entities.Shared.MatterName')" v-slot="vp" slim>
                    <b-form-group :label="$t('Entities.Shared.MatterName')"
                                  label-for="matterName"
                                  class="col-md-5">
                        <b-form-input name="matterName"
                                      maxlength="500"
                                      :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                      v-model="matterName"
                                      :state="vp.errors[0] ? false : null">
                        </b-form-input>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>

                <b-form-group label="Presiding Officer/s"
                              label-for="courtJudgeIds"
                              class="col-md-4">
                    <select2 ref="courtJudgeIds"
                             name="courtJudgeIds"
                             :options="initialCourtJudges"
                             enable-ajax
                             multiple
                             key-property="courtJudgeId"
                             text-property="displayName"
                             :placeholder="(readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC) ? null : courtRegistryId ? 'Select one or more presiding officer/s' : 'Select a court/tribunal/other first'"
                             :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC || (courtRegistryId == null)"
                             v-model="currentCourtJudgeIds"
                             v-on:search="findJudges">
                    </select2>
                </b-form-group>
            </b-form-row>
            <!--Sitting Dates-->
            <b-form-row class="mb-3" v-if="isUuidNullOrEmpty(orderRequest.clientOrderRequestId)">
                <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-start">
                        <h5 class="card-title">Sitting Dates</h5>
                        <b-dropdown id="addCourtSittingButton" size="sm" variant="outline-secondary" split right v-on:click="addCourtSitting" :disabled="courtRegistryId == null" v-if="!readonly">
                            <template v-slot:button-content>
                                <i class="fas fa-plus mr-1"></i>Add
                            </template>
                            <b-dropdown-item v-on:click="addCourtSitting">Single Sitting</b-dropdown-item>
                            <b-dropdown-item v-on:click="addCourtSittings">Multiple Sittings</b-dropdown-item>
                        </b-dropdown>
                    </div>

                    <b-table striped hover fixed small :items="currentSittingDates" :fields="sittingDateFields" class="mb-0">
                        <template #cell(action)="row">
                            <b-button size="sm" v-if="row.item.new" style="font-size: 10px;" @click="removeSittingDateFromList(row.index)" class="p1" variant="danger">
                                Remove
                            </b-button>
                        </template>
                    </b-table>

                    <validation-observer v-slot="vp" slim>
                        <b-modal :size="!addRange ? 'md' : 'lg'"
                                 centered
                                 no-close-on-backdrop
                                 no-close-on-esc
                                 v-model="dialogOpen"
                                 v-on:ok.prevent="vp.handleSubmit(newSittingDate)">

                            <template #modal-header>
                                <h5 class="modal-title">New Sitting Date</h5>
                            </template>

                            <template #default>
                                <b-form-row>
                                    <validation-provider ref="multipleSittingDatesValidationProvider"
                                                         rules="required"
                                                         name="Sitting Date Range"
                                                         v-slot="{ valid, errors }"
                                                         v-if="addRange"
                                                         slim>
                                        <b-form-group label="Sitting Date Range"
                                                      label-for="currentSittingDate_sittingDateRange"
                                                      class="col-md-6">
                                            <ejs-daterangepicker name="currentSittingDate_sittingDateRange"
                                                                 v-model="currentSittingDate.sittingDateRange"
                                                                 format="dd/MM/yyyy">
                                            </ejs-daterangepicker>
                                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                    <validation-provider ref="sittingDateValidationProvider"
                                                         rules="required"
                                                         name="Sitting Date"
                                                         v-slot="{ valid, errors }"
                                                         v-if="!addRange"
                                                         slim>
                                        <b-form-group label="Sitting Date"
                                                      label-for="currentSittingDate_sittingDate"
                                                      class="col-md-4">
                                            <ejs-datepicker name="currentSittingDate_sittingDate"
                                                            v-model="currentSittingDate.sittingDate"
                                                            format="d/M/yyyy">
                                            </ejs-datepicker>
                                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                    <b-form-group label="Start Time"
                                                  label-for="currentSittingDate_startTime"
                                                  :class="!addRange ? 'col-md-4' : 'col-md-3'">
                                        <b-form-input name="currentSittingDate_startTime"
                                                      type="time"
                                                      v-model="currentSittingDate.startTime">
                                        </b-form-input>
                                    </b-form-group>
                                    <b-form-group label="End Time"
                                                  label-for="currentSittingDate_endTime"
                                                  :class="!addRange ? 'col-md-4' : 'col-md-3'">
                                        <b-form-input name="currentSittingDate_endTime"
                                                      type="time"
                                                      v-model="currentSittingDate.endTime">
                                        </b-form-input>
                                    </b-form-group>
                                </b-form-row>
                                <b-form-row v-if="addRange">
                                    <b-form-checkbox name="excludeWeekends"
                                                     v-model="excludeWeekends"
                                                     v-if="addRange"
                                                     switch>
                                        Exclude Weekends
                                    </b-form-checkbox>
                                </b-form-row>

                                <b-form-row>
                                    <b-form-group label="Location/Court Room"
                                                  label-for="currentSittingDate_courtRoomId"
                                                  :class="!addRange ? 'col-md-10' : 'col-md-8'">
                                        <select2 name="currentSittingDate_courtRoomId"
                                                 placeholder="Select a room"
                                                 enable-ajax
                                                 allow-clear
                                                 in-b-modal
                                                 key-property="courtRoomId"
                                                 text-property="courtLocationRoomName"
                                                 v-model="currentSittingDate.courtRoomId"
                                                 v-on:search="findCourtRooms"
                                                 v-on:select="courtRoomSelected">
                                        </select2>
                                        <b-form-input v-if="sittingLocationOtherEnabled"
                                                      class="mt-2"
                                                      placeholder="Type in a room name."
                                                      name="currentSittingDate_courtRoomOther"
                                                      type="text"
                                                      v-model="currentSittingDate.courtRoomOther">
                                        </b-form-input>
                                    </b-form-group>
                                </b-form-row>


                                <b-form-row>
                                    <b-form-group :label="$t('Entities.Shared.CourtSittingMatterType')"
                                                  label-for="currentSittingDate_courtSittingMatterType"
                                                  :class="!addRange ? 'col-md-10' : 'col-md-8'">
                                        <select2 name="currentSittingDate_courtSittingMatterType"
                                                 :placeholder="$t('Vue.Shared.Select2Placeholder.CourtSittingMatterType')"
                                                 allow-clear
                                                 in-b-modal
                                                 key-property="courtSittingMatterTypeId"
                                                 text-property="name"
                                                 v-model="currentSittingDate.courtSittingMatterTypeId"
                                                 :options="allCourtSittingMatterTypes"
                                                 disable-search
                                                 v-on:select="courtSittingMatterTypeSelected">
                                        </select2>
                                    </b-form-group>
                                </b-form-row>
                            </template>

                            <template #modal-footer="{ ok, cancel }">
                                <button type="button" class="btn btn-secondary" @click="cancel()">Cancel</button>
                                <button type="button" class="btn btn-primary" @click="ok()">Create</button>
                            </template>
                        </b-modal>
                    </validation-observer>
                </div>
            </b-form-row>
            <template v-else>
                <b-form-row>
                    <validation-provider rules="required"
                                         name="Sitting Date"
                                         v-slot="{ valid, errors }"
                                         slim>
                        <b-form-group label="Sitting Date"
                                      label-for="sittingDate"
                                      class="col-md-2">
                            <ejs-datepicker name="sittingDate"
                                            v-model="currentSittingDate.sittingDate"
                                            format="d/M/yyyy"
                                            :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC">
                            </ejs-datepicker>
                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <b-form-group label="Start Time"
                                  label-for="startTime"
                                  class="col-md-2">
                        <b-form-input ref="startTimeInput"
                                      name="startTime"
                                      type="time"
                                      :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                      v-model="currentSittingDate.startTime">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group label="End Time"
                                  label-for="endTime"
                                  class="col-md-2">
                        <b-form-input name="endTime"
                                      type="time"
                                      :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                      v-model="currentSittingDate.endTime">
                        </b-form-input>
                    </b-form-group>

                    <b-form-group label="Location/Court Room"
                                  label-for="courtRoomId"
                                  class="col-md-3">
                        <select2 name="courtRoomId"
                                 :placeholder="(readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC) ? null : 'Select a room'"
                                 enable-ajax
                                 allow-clear
                                 in-b-modal
                                 key-property="courtRoomId"
                                 text-property="courtLocationRoomName"
                                 v-model="currentSittingDate.courtRoomId"
                                 :options="initialCourtRooms"
                                 :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                 v-on:search="findCourtRooms"
                                 v-on:select="courtRoomSelected">
                        </select2>
                    </b-form-group>

                    <b-form-group :label="$t('Entities.Shared.CourtSittingMatterType')"
                                  label-for="courtSittingMatterType"
                                  class="col-md-3">
                        <select2 name="courtSittingMatterType"
                                 :placeholder="(readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC) ? null : $t('Vue.Shared.Select2Placeholder.CourtSittingMatterType')"
                                 allow-clear
                                 in-b-modal
                                 key-property="courtSittingMatterTypeId"
                                 text-property="name"
                                 v-model="currentSittingDate.courtSittingMatterTypeId"
                                 :options="allCourtSittingMatterTypes"
                                 disable-search
                                 :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                 v-on:select="courtSittingMatterTypeSelected">
                        </select2>
                    </b-form-group>
                </b-form-row>
            </template>
            <!--Service Type / Service Delay-->
            <b-form-row>
                <validation-provider ref="serviceTypeSelectValidator" rules="required" name="Service Type" v-slot="vp" slim>
                    <b-form-group label="Service Type"
                                  label-for="serviceTypeId"
                                  class="col-md-4">
                        <select2 ref="serviceTypeSelect"
                                 name="serviceTypeId"
                                 v-model="serviceTypeId"
                                 :options="currentSelectableServiceTypes"
                                 disable-search
                                 key-property="serviceTypeId"
                                 text-property="name"
                                 :placeholder="courtRegistryId ? 'Select a service type' : 'Select a court/tribunal/other first'"
                                 :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC || (courtRegistryId == null)"
                                 v-on:select="serviceTypeSelected"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <validation-provider ref="serviceDelaySelectValidator" rules="required" name="Turnaround" v-slot="vp" slim v-if="serviceTypeRequiresServiceDelay">
                    <b-form-group label="Turnaround"
                                  label-for="serviceDelayId"
                                  class="col-md-4">
                        <select2 ref="serviceDelaySelect"
                                 name="serviceDelayId"
                                 v-model="serviceDelayId"
                                 :options="currentSelectableServiceDelays"
                                 disable-search
                                 key-property="serviceDelayId"
                                 text-property="name"
                                 placeholder="Select a service delay"
                                 :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
            </b-form-row>
            <!--Default Distribution List-->
            <b-form-row v-if="!hideDistributionList && !serviceTypeHideDistributionList">
                <b-form-group label="Distribution List"
                              label-for="deliveryContactIds"
                              class="col">
                    <contact-select name="deliveryContactIds"
                                    :api-url="$urlHelper.getApiUrl('contact','delivery')"
                                    :ref-data-api-controller-url="$urlHelper.getApiUrl('referencedata')"
                                    :client-id="orderingClientId"
                                    :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                    v-model="currentDeliveryContactIds"
                                    :initial-contacts="initialDeliveryContacts"
                                    multiple>
                    </contact-select>
                </b-form-group>
            </b-form-row>
            <b-form-row v-if="!hideDistributionList && !serviceTypeHideDistributionList">
                <b-form-group label="Distribution CC List"
                              label-for="deliveryContactCcIds"
                              class="col">
                    <contact-select name="deliveryContactCcIds"
                                    :api-url="$urlHelper.getApiUrl('contact','delivery')"
                                    :ref-data-api-controller-url="$urlHelper.getApiUrl('referencedata')"
                                    :client-id="orderingClientId"
                                    :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                    v-model="currentDeliveryContactCcIds"
                                    :initial-contacts="initialDeliveryContacts"
                                    multiple>
                    </contact-select>
                </b-form-group>
            </b-form-row>
            <b-form-row v-if="!hideDistributionList && !serviceTypeHideDistributionList">
                <b-form-group label="Distribution BCC List"
                              label-for="deliveryContactBccIds"
                              class="col">
                    <contact-select name="deliveryContactBccIds"
                                    :api-url="$urlHelper.getApiUrl('contact','delivery')"
                                    :ref-data-api-controller-url="$urlHelper.getApiUrl('referencedata')"
                                    :client-id="orderingClientId"
                                    :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                    v-model="currentDeliveryContactBccIds"
                                    :initial-contacts="initialDeliveryContacts"
                                    multiple>
                    </contact-select>
                </b-form-group>
            </b-form-row>
            <!--Comments-->
            <b-form-row>
                <b-form-group label="Comments"
                              label-for="comments"
                              class="col-md-12">
                    <b-form-textarea name="comments"
                                     rows="3"
                                     max-rows="6"
                                     :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                     v-model="comments">
                    </b-form-textarea>
                </b-form-group>
            </b-form-row>
            <!--Attachments-->
            <attachments v-model="currentAttachments"
                         :all-attachment-classifications="allAttachmentClassifications"
                         :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                         attachment-classification-selection="required"
                         :can-add-attachments="(allAttachmentClassifications != null) && (allAttachmentClassifications.length > 0)"
                         v-if="canViewAttachments">
            </attachments>

            <b-form-row>
                <b-form-group class="col-md-4">
                    <b-form-checkbox name="awaitingFurtherAttachments"
                                     v-model="awaitingFurtherAttachments"
                                     class="mb-2"
                                     switch
                                     :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC">
                        Awaiting Further Attachments
                    </b-form-checkbox>
                </b-form-group>
            </b-form-row>

            <validation-summary ref="validationSummary" :vee-validate-observer-errors="vo.errors"></validation-summary>

            <div class="btn-toolbar justify-content-end mt-2" role="toolbar" aria-label="Action buttons">
                <div class="btn-group" role="group">
                    <b-button variant="outline-success"
                              v-on:click.prevent="trySaveOrder('close')"
                              v-if="!readonly && canEditOrderRequest && (orderRequest.submittedDateTimeUTC == null)">
                        <i class="fas fa-check mr-1"></i>Save and Close
                    </b-button>
                    <b-button variant="outline-success"
                              v-on:click.prevent="showTerms"
                              v-if="!readonly && canEditOrderRequest && (orderRequest.submittedDateTimeUTC == null) && (awaitingFurtherAttachments == false)">
                        <i class="fas fa-check mr-1"></i>Submit Order Request
                    </b-button>
                    <b-button :href="$urlHelper.getPageUrl('OrderRequests/Index?initialTab=Court')"
                              :variant="(readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC) ? 'outline-secondary' : 'outline-danger'">
                        <i :class="(readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC) ? 'fas fa-arrow-left mr-1' : 'fas fa-times mr-1'"></i>
                        {{ (readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC) ? 'Back' : 'Cancel' }}
                    </b-button>
                </div>
            </div>
        </validation-observer>

        <b-input-group size="sm" prepend="Created At" class="mt-2" v-if="!isUuidNullOrEmpty(orderRequest.clientOrderRequestId)">
            <b-form-input :value="orderRequest.createdDateTimeUTC | formatDateTime('DD/MM/YYYY h:mmA')" readonly></b-form-input>
            <b-input-group-prepend is-text class="input-group-append">By</b-input-group-prepend>
            <b-form-input :value="orderRequest.createdByUserAccount.displayName" readonly></b-form-input>
            <b-input-group-prepend is-text class="input-group-append">Last Update At</b-input-group-prepend>
            <b-form-input :value="orderRequest.lastUpdateDateTimeUTC | formatDateTime('DD/MM/YYYY h:mmA')" readonly></b-form-input>
            <b-input-group-prepend is-text class="input-group-append">By</b-input-group-prepend>
            <b-form-input :value="(orderRequest.lastUpdateByUserAccount != null) ? orderRequest.lastUpdateByUserAccount.displayName : null" readonly></b-form-input>
        </b-input-group>

        <validation-observer v-slot="vo1" slim>
            <b-modal size="md"
                     centered
                     no-close-on-backdrop
                     no-close-on-esc
                     title="Submit Order Request"
                     v-model="acceptTermsDialogShown"
                     v-on:ok.prevent="vo1.handleSubmit(trySaveOrder('submit'))">
                <!--v-on:ok="trySaveOrder('submit')">-->
                <template #default>
                    <b-form-row>
                        <validation-provider :rules="{ required: { allowFalse: false } }"
                                             name="Accept Terms"
                                             v-slot="{ valid, errors }"
                                             slim
                                             :custom-messages="{ required: 'You have to agree to the terms of use' }">
                            <b-form-group>
                                <b-form-checkbox name="acceptTerms"
                                                 class="mb-2"
                                                 switch
                                                 v-model="acceptTerms"
                                                 :state="errors[0] ? false : null">
                                    By submitting this order, I agree to the <a href="https://www.epiqglobal.com/en-au/terms-of-use-au" target="_blank">Terms of Use</a>
                                </b-form-checkbox>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                    </b-form-row>
                </template>
                <template #modal-footer="{ ok, cancel }">
                    <button type="button" class="btn btn-secondary" @click="cancel()">No, Do Not Submit</button>
                    <button type="button" class="btn btn-primary" @click="ok()">Yes, Agree and Submit</button>
                </template>
            </b-modal>
        </validation-observer>

    </div>
</template>

<script>
    import $ from 'jquery';
    import Vue from 'vue';
    import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue';

    import axios from 'axios';
    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import UuidMixins from '../../mixins/UuidMixins';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';

    import { extend, localize } from 'vee-validate';
    import { required, required_if } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';

    extend('required', required);
    extend('required_if', required_if);
    localize('en', en);

    Vue.use(BootstrapVue);
    Vue.use(BootstrapVueIcons);

    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);

    export default {
        name: "CourtOrderRequestDetails",
        mixins: [ErrorsMixin, DateTimeMixin, UuidMixins],
        filters: DateTimeMixin.filter,
        props: {
            readonly: {
                type: Boolean,
                default: false
            },
            canEditOrderRequest: {
                type: Boolean,
                default: true
            },
            canViewAttachments: {
                type: Boolean,
                default: true
            },
            orderRequest: {
                type: Object,
                required: true
            },
            initialBillingContact: {
                type: Object,
                default: null
            },
            promptForBillingContact: {
                type: Boolean,
                default: null
            },
            allCourtOrderClientParticipationInMatters: {
                type: Array,
                default: null
            },
            allCourtRegistries: {
                type: Array,
                default: null
            },
            courtRegistryList: {
                type: Array,
                default: null
            },
            initialCourtJurisdictions: {
                type: Array,
                default: null
            },
            courtJudgeIds: {
                type: Array,
                default: null
            },
            initialCourtJudges: {
                type: Array,
                default: null
            },
            initialSittingDates: {
                type: Array,
                default: null
            },
            initialCourtRooms: {
                type: Array,
                default: null
            },
            initialCourtSittingMatterTypes: {
                type: Array,
                default: null
            },
            initialServiceTypes: {
                type: Array,
                default: null
            },
            initialServiceDelays: {
                type: Array,
                default: null
            },
            deliveryContactIds: {
                type: Array,
                default: null
            },
            deliveryContactCcIds: {
                type: Array,
                default: null
            },
            deliveryContactBccIds: {
                type: Array,
                default: null
            },
            initialDeliveryContacts: {
                type: Array,
                default: null
            },
            initialDeliveryCcContacts: {
                type: Array,
                default: null
            },
            initialDeliveryBccContacts: {
                type: Array,
                default: null
            },
            allAttachmentClassifications: {
                type: Array,
                required: true
            },
            attachments: {
                type: Array,
                default: null
            },
            selectableClientBranches: {
                type: Array,
                default: null
            }
        },

        data() {
            return {
                orderingClientId: this.orderRequest.orderingClient.clientId,
                orderingClient: this.orderRequest.orderingClient.name,
                orderingClientBranchId: this.isUuidNullOrEmpty(this.orderRequest.orderingClientBranchId) ? null : this.orderRequest.orderingClientBranchId,
                orderingClientContactId: this.orderRequest.orderingClientContact.contactId,
                orderingClientContact: this.orderRequest.orderingClientContact.name,
                clientReference: this.orderRequest.clientReference,
                separateBillingContact: (this.isUuidNullOrEmpty(this.orderRequest.clientOrderRequestId) && this.promptForBillingContact) || (!this.isUuidNullOrEmpty(this.orderRequest.clientOrderRequestId) && (this.initialBillingContact != null)),
                billingContactId: this.orderRequest.billingContactId,
                clientParticipationInMatter: this.orderRequest.clientParticipationInMatter ? this.orderRequest.clientParticipationInMatterString : null,
                courtRegistryId: this.isUuidNullOrEmpty(this.orderRequest.courtRegistryId) ? null : this.orderRequest.courtRegistryId,
                courtJurisdictionId: this.isUuidNullOrEmpty(this.orderRequest.courtJurisdictionId) ? null : this.orderRequest.courtJurisdictionId,
                matterNumber: this.orderRequest.matterNumber,
                matterName: this.orderRequest.matterName,
                currentCourtJudgeIds: this.courtJudgeIds,
                currentErrors: [],
                dialogOpen: false,
                addRange: false,
                newSittingsDateRange: null,
                currentSittingDate:
                    this.isUuidNullOrEmpty(this.orderRequest.clientOrderRequestId) || (this.initialSittingDates == null) || (this.initialSittingDates.length == 0) ?
                    {
                        sittingDate: null,
                        sittingDateRange: null,
                        startTime: null,
                        endTime: null,
                        courtRoomId: null,
                        courtRoom: null,
                        courtSittingMatterTypeId: null,
                        courtSittingMatterType: null,
                            courtRoomOther: null,
                        }
                        : this.initialSittingDates[0].courtRoomOther ?
                            {
                                ...this.initialSittingDates[0],
                                courtRoomId: "00000000-0000-0000-0000-000000000000",
                            }
                            : this.initialSittingDates[0],
                serviceTypeId: this.isUuidNullOrEmpty(this.orderRequest.serviceTypeId) ? null : this.orderRequest.serviceTypeId,
                currentSelectableServiceTypes: this.initialServiceTypes,
                serviceTypeRequiresServiceDelay: (this.orderRequest.serviceType == null) ? false : this.orderRequest.serviceType.requiresServiceDelay,
                serviceDelayId: this.isUuidNullOrEmpty(this.orderRequest.serviceDelayId) ? null : this.orderRequest.serviceDelayId,
                currentSelectableServiceDelays: this.initialServiceDelays,
                currentDeliveryContactIds: this.deliveryContactIds,
                currentDeliveryContactCcIds: this.deliveryContactCcIds,
                currentDeliveryContactBccIds: this.deliveryContactBccIds,
                comments: this.orderRequest.comments,
                currentAttachments: this.attachments,
                awaitingFurtherAttachments: this.orderRequest.awaitingFurtherAttachments,

                allCourtSittingMatterTypes: this.initialCourtSittingMatterTypes,
                currentSittingDates: this.initialSittingDates,
                acceptTermsDialogShown: false,
                acceptTerms: false,
                hideDistributionList: false,
                serviceTypeHideDistributionList: (this.orderRequest.serviceType == null) ? false : this.orderRequest.serviceType.hideDistributionList,
                excludeWeekends: true,
                sittingLocationOtherEnabled: false,
            };
        },
        watch: {
            separateBillingContact: function () {
                if (!this.separateBillingContact) {
                    this.billingContactId = null;
                }
            },
        },
        computed: {
            sittingDateFields: function () {
                return [
                    {
                        key: 'sittingDate',
                        label: 'Sitting Date',
                        class: 'text-left ',
                        formatter: (value) => {
                            return value ? this.formatDateTime(value, 'DD/MM/YYYY', true, true) : value;
                        }
                    },
                    {
                        key: 'startTime',
                        label: 'Start Time',
                        class: 'text-left ',
                        formatter: (value) => {
                            return value ? this.formatDateTime(value, 'h:mmA', true, true) : value;
                        }
                    },
                    {
                        key: 'endTime',
                        label: 'End Time',
                        class: 'text-left ',
                        formatter: (value) => {
                            return value ? this.formatDateTime(value, 'h:mmA', true, true) : value;
                        }
                    },
                    {
                        key: 'courtRoom',
                        label: 'Court Room',
                        class: ' text-left'
                    },
                    {
                        key: 'courtSittingMatterType',
                        label: this.$t('Entities.Shared.CourtSittingMatterType'),
                        class: ' text-left'
                    },
                    {
                        key: 'action',
                        label: '',
                        class: 'text-right'
                    }
                ];
            },
            mandatoryContactProperties: function () {
                if (!this.$config.enableRegionStateOptions) {
                    return ['address', 'suburb', 'postcode', 'country'];
                }
                else {
                    return ['address', 'suburb', 'state', 'postcode', 'country'];
                }
            }
        },
        methods: {
            courtRegistrySelected: function () {
                this.currentCourtJudgeIds = null;
                this.currentSittingDates = [];
                this.serviceTypeId = null;
                this.serviceDelayId = null;

                if (this.courtRegistryId != null) {
                    var courtRegistry = this.courtRegistryList.find(element => element.courtRegistryId == this.courtRegistryId);

                    if (courtRegistry != null) {
                        this.hideDistributionList = courtRegistry.hideDistributionList;
                    }
                }
                else {
                    this.hideDistributionList = false;
                }

                this.reloadCourtSittingMatterTypes();
                this.reloadServiceTypes();
                this.reloadServiceDelays();
                this.courtJurisdictionId = null;
                this.findJurisdictions();
            },
            reloadCourtSittingMatterTypes: function () {
                if (this.courtRegistryId) {
                    var url = this.$urlHelper.getApiUrl('courtsittingmattertype', 'bycourtregistry', [this.courtRegistryId]);
                    axios
                        .get(url)
                        .then((response) => {
                            this.allCourtSittingMatterTypes = response.data;
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.allCourtSittingMatterTypes = null;
                }
            },
            reloadServiceTypes: function () {
                if (this.courtRegistryId) {
                    var url = this.$urlHelper.getApiUrl('servicetype', 'bycourtregistry', [this.courtRegistryId]);
                    axios
                        .get(url)
                        .then((response) => {
                            this.currentSelectableServiceTypes = response.data;
                            Vue.nextTick().then(() => {
                                this.$refs.serviceTypeSelect.refresh();
                                this.$refs.serviceTypeSelectValidator.reset();
                            });
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.currentSelectableServiceTypes = null;
                }
            },
            reloadServiceDelays: function () {
                if (this.courtRegistryId) {
                    var url = this.$urlHelper.getApiUrl('servicedelay', 'bycourtregistry', [this.courtRegistryId]);
                    axios
                        .get(url)
                        .then((response) => {
                            this.currentSelectableServiceDelays = response.data;
                            Vue.nextTick().then(() => {
                                this.$refs.serviceDelaySelect.refresh();
                            });
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.currentSelectableServiceDelays = null;
                }
            },
            findJudges: function (params, success, failure) {
                if (this.courtRegistryId) {
                    var filter = {
                        searchTerm: ''
                    };
                    if (typeof (params.data.term) != "undefined")
                        filter.searchTerm = params.data.term;
                    var url = this.$urlHelper.getApiUrl('courtjudge', 'bycourtregistry', [this.courtRegistryId, 'find']);
                    axios
                        .post(url, JSON.stringify(filter),
                            {
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            })
                        .then(function (response) {
                            var mappedData = response.data;
                            if (mappedData != null) {
                                mappedData = mappedData.map((obj) => {
                                    obj.id = obj.courtJudgeId;
                                    obj.text = obj.displayName;
                                    return obj;
                                });
                            }
                            success({ results: mappedData });
                        })
                        .catch(function (error) {
                            failure(error);
                        });
                }
            },
            findJurisdictions: function (params, success, failure) {
                var filter = {
                    searchTerm: ''
                };
                if (typeof (params.data.term) != "undefined")
                    filter.searchTerm = params.data.term;

                var url = this.$urlHelper.getApiUrl('courtjurisdiction', 'bycourtregistry', [this.courtRegistryId]);
                url += '/find';
                console.log('url: ' + url)
                axios
                    .post(url, JSON.stringify(filter),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        var mappedData = response.data;
                        if (mappedData != null) {
                            mappedData = $.map(mappedData, function (obj) {
                                obj.id = obj.courtJurisdictionId;
                                obj.text = obj.name;
                                return obj;
                            });
                        }
                        success({ results: mappedData });
                    })
                    .catch(function (error) {
                        failure(error);
                    });
            },
            courtJurisdictionSelected: function () {
                //this.reloadDefaultDeliveryContacts();
            },
            showModal: function () {
                this.newSittingsDateRange = null;
                this.currentSittingDate.sittingDate = null;
                this.currentSittingDate.startSittingDate = null;
                this.currentSittingDate.endSittingDate = null;
                this.currentSittingDate.startTime = null;
                this.currentSittingDate.endTime = null;
                this.currentSittingDate.courtRoomId = null;
                this.currentSittingDate.courtRoom = null;
                this.currentSittingDate.courtRoomOther = null;
                this.currentSittingDate.courtSittingMatterTypeId = null;
                this.currentSittingDate.courtSittingMatterType = null;
                this.sittingLocationOtherEnabled = false;
                this.dialogOpen = true;
                Vue.nextTick().then(() => {
                    if (this.addRange)
                        this.resetDateValidationProviders('multiple');
                    else
                        this.resetDateValidationProviders('single');
                });


            },
            findCourtRooms: function (params, success, failure) {
                var filter = {
                    searchTerm: ''
                };
                if (typeof (params.data.term) != "undefined")
                    filter.searchTerm = params.data.term;
                var url = this.$urlHelper.getApiUrl('courtroom', 'bycourtregistry', [this.courtRegistryId, 'find']);
                axios
                    .post(url, JSON.stringify(filter),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        var mappedData = response.data;
                        if (mappedData != null) {
                            mappedData = mappedData.map((obj) => {
                                obj.id = obj.courtRoomId;
                                obj.text = obj.courtLocationRoomName;
                                return obj;
                            });
                        }
                        mappedData.push({ id: "00000000-0000-0000-0000-000000000000", text: "OTHER" })
                        success({ results: mappedData });
                    })
                    .catch(function (error) {
                        failure(error);
                    });
            },
            courtRoomSelected: function (courtRoom) {
                this.sittingLocationOtherEnabled = courtRoom && (courtRoom.text == "OTHER");
                this.currentSittingDate.courtRoom = courtRoom ? courtRoom.courtLocationRoomName : null;
            },
            courtSittingMatterTypeSelected: function (courtSittingMatterType) {
                this.currentSittingDate.courtSittingMatterType = courtSittingMatterType ? courtSittingMatterType.name : null;
            },
            newSittingDate: function () {
                var newSittingDateObject = {};
                Object.assign(newSittingDateObject, this.currentSittingDate);
                if (this.addRange == true) {
                    var startDate = newSittingDateObject.sittingDateRange[0];
                    var endDate = newSittingDateObject.sittingDateRange[1];
                    var currentDate = startDate;

                    do {
                        //check if not weekend
                        if (!this.excludeWeekends || this.getDayOfWeek(currentDate) <= 5) {
                            this.addSittingDate(currentDate);
                        }

                        currentDate = this.addDays(currentDate, 1);
                    } while (currentDate <= endDate);
                }
                else {
                    this.addSittingDate(newSittingDateObject.sittingDate);
                }

            },
            addSittingDate: function (sittingDate) {
                var newSittingDateObject = {};
                Object.assign(newSittingDateObject, this.currentSittingDate);
                if (sittingDate) {
                    //the input from the UI control has a time of 00:00:00 but the local timezone, to get rid of the timezone we have to
                    //convert the date to a string first and then create our own date/time object from it
                    newSittingDateObject.sittingDate = this.parseDateTime(this.formatDateTime(sittingDate, 'DD/MM/YYYY'), 'DD/MM/YYYY');
                }
                if (newSittingDateObject.startTime != null) {
                    newSittingDateObject.startTime = this.parseDateTime(newSittingDateObject.startTime, 'HH:mm', true, true);
                }
                if (newSittingDateObject.endTime != null) {
                    newSittingDateObject.endTime = this.parseDateTime(newSittingDateObject.endTime, 'HH:mm', true, true);
                }
                newSittingDateObject.new = true;
                if (this.currentSittingDates == null) this.currentSittingDates = [];
                this.currentSittingDates.push(newSittingDateObject);
                if (this.newSittingDates == null) this.newSittingDates = [];
                this.newSittingDates.push(newSittingDateObject);
                this.$emit('change', this.newSittingDates);
                this.dialogOpen = false;
                console.log(this.newSittingDates);
            },

            removeSittingDateFromList: function (index) {
                if (this.newSittingDates != null) {
                    var existingSittingDatesCount = this.currentSittingDates.length - this.newSittingDates.length;
                    if (index >= existingSittingDatesCount) {
                        //the index refers to a new payment, we also have to remove it from the newSittingDates
                        var newSittingDatesIndex = index - existingSittingDatesCount;
                        this.newSittingDates.splice(newSittingDatesIndex, 1);
                        this.$emit('change', this.newSittingDates);
                    }
                }
                this.currentSittingDates.splice(index, 1);
            },
            serviceTypeSelected: function (serviceType) {
                if (serviceType != null) {
                    this.serviceTypeRequiresServiceDelay = serviceType.requiresServiceDelay;
                    if (!this.serviceTypeRequiresServiceDelay) {
                        this.serviceDelayId = null;
                    }
                    this.serviceTypeHideDistributionList = serviceType.hideDistributionList;
                }
                else {
                    this.serviceTypeRequiresServiceDelay = false;
                    this.serviceDelayId = null;
                    this.serviceTypeHideDistributionList = false;
                }
            },
            addCourtSitting: function () {
                this.addRange = false;
                this.showModal();
            },
            addCourtSittings: function () {
                this.addRange = true;
                this.showModal();
            },
            resetDateValidationProviders: function (typeOfDateSelection) {
                if (typeOfDateSelection == 'single') this.$refs.sittingDateValidationProvider.reset();
                if (typeOfDateSelection == 'multiple') this.$refs.multipleSittingDatesValidationProvider.reset();
            },
            trySaveOrder: function (actionAfterSave) {
                if (actionAfterSave == "submit" && this.acceptTerms != true) {
                    return;
                }

                var thisVueApp = this;
                this.$refs.validationObserver
                    .validate()
                    .then(function (isValid) {
                        if (isValid) thisVueApp.saveOrderRequest(actionAfterSave);
                        else thisVueApp.$refs.validationSummary.activate();
                    });
            },
            saveOrderRequest: function (actionAfterSave) {
                //console.log('courtOrder: ' + this.orderingClientId);
                var data = {
                    clientOrderRequestId: this.orderRequest.clientOrderRequestId,
                    orderRequestNumber: this.orderRequest.orderRequestNumber,
                    orderingClientId: this.orderingClientId,
                    orderingClientBranchId: this.orderingClientBranchId,
                    orderingClientContactId: this.orderingClientContactId,
                    clientReference: this.clientReference,
                    billingContactId: this.billingContactId,
                    clientParticipationInMatter: this.clientParticipationInMatter,
                    courtRegistryId: this.courtRegistryId,
                    courtJurisdictionId: this.courtJurisdictionId,
                    matterNumber: this.matterNumber,
                    matterName: this.matterName,
                    courtJudgeIds: this.currentCourtJudgeIds,
                    clientCourtOrderRequestSittingDateDetails: this.currentSittingDates,
                    serviceTypeId: this.serviceTypeId,
                    serviceDelayId: this.serviceDelayId,
                    deliveryContactIds: null,
                    deliveryContactCcIds: null,
                    deliveryContactBccIds: null,
                    comments: this.comments,
                    attachments: [],
                    awaitingFurtherAttachments: this.awaitingFurtherAttachments,
                };

                if (!this.hideDistributionList && !this.serviceTypeHideDistributionList) {
                    data.deliveryContactIds = this.currentDeliveryContactIds;
                    data.deliveryContactCcIds = this.currentDeliveryContactCcIds;
                    data.deliveryContactBccIds = this.currentDeliveryContactBccIds;
                }


                if (!this.isUuidNullOrEmpty(data.clientOrderRequestId) && data.clientCourtOrderRequestSittingDateDetails && data.clientCourtOrderRequestSittingDateDetails.length) {
                    //this is an existing entry, the sitting date has been displayed/changed via the single UI control, we need to convert it properly here
                    if (data.clientCourtOrderRequestSittingDateDetails[0].sittingDate) {
                        //the input from the UI control has a time of 00:00:00 but the local timezone, to get rid of the timezone we have to
                        //convert the date to a string first and then create our own date/time object from it
                        data.clientCourtOrderRequestSittingDateDetails[0].sittingDate = this.parseDateTime(this.formatDateTime(data.clientCourtOrderRequestSittingDateDetails[0].sittingDate, 'DD/MM/YYYY'), 'DD/MM/YYYY');
                    }
                    if (data.clientCourtOrderRequestSittingDateDetails[0].startTime != null) {
                        data.clientCourtOrderRequestSittingDateDetails[0].startTime = this.parseDateTime(data.clientCourtOrderRequestSittingDateDetails[0].startTime, 'HH:mm', true, true);
                    }
                    if (data.clientCourtOrderRequestSittingDateDetails[0].endTime != null) {
                        data.clientCourtOrderRequestSittingDateDetails[0].endTime = this.parseDateTime(data.clientCourtOrderRequestSittingDateDetails[0].endTime, 'HH:mm', true, true);
                    }
                }
                if (this.currentAttachments != null) {
                    this.currentAttachments.forEach(function (attachment) {
                        data.attachments.push({ fileId: attachment.fileId, classification: attachment.classificationString, copyOnSave: attachment.copyOnSave });
                    });
                }

                if (actionAfterSave == 'submit') {
                    data.clientOrderRequestStatus = 'Submitted';
                }

                this.currentErrors = [];
                var url = this.$urlHelper.getApiUrl('ClientOrderRequest', 'court');
                var thisVueApp = this;
                axios
                    .request(
                        {
                            method: (this.orderId == null) ? 'post' : 'put',
                            url: url,
                            data: JSON.stringify(data),
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        if (actionAfterSave == 'submit') {
                            window.location.replace(thisVueApp.$urlHelper.getPageUrl("OrderRequests/Index?initialTab=Court"));
                        }
                        else if (actionAfterSave == 'close') {
                            window.location.replace(thisVueApp.$urlHelper.getPageUrl("OrderRequests/Index?initialTab=Court"));
                        }
                    })
                    .catch((ex) => {
                        thisVueApp.currentErrors = thisVueApp.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (thisVueApp.currentErrors.length != 0) {
                            thisVueApp.showErrorsToast(thisVueApp.currentErrors);
                        }
                    });
            },
            showTerms: function () {
                this.acceptTerms = false;
                this.acceptTermsDialogShown = true;
            },
        },
        mounted() {
            if (!this.isUuidNullOrEmpty(this.orderRequest.clientOrderRequestId) && this.currentSittingDate != null) {
                if (this.currentSittingDate.startTime) this.currentSittingDate.startTime = this.formatDateTime(this.currentSittingDate.startTime, 'HH:mm', true, true);
                if (this.currentSittingDate.endTime) this.currentSittingDate.endTime = this.formatDateTime(this.currentSittingDate.endTime, 'HH:mm', true, true);
            }
        }
    };
</script>



